import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, Loader2, Store, HelpCircle, Mail, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateOrganizationSlug } from '@/hooks/useOrganizationFromSubdomain';
import { redirectToSubdomain, isDevelopment, setDevOrg, isElectron } from '@/lib/subdomain';
import { showIntercomAnonymous } from '@/components/IntercomProvider';
import { supabase } from '@/lib/supabase';

const storeSchema = z.object({
  storeUrl: z.string()
    .min(1, 'Store URL is required')
    .regex(/^[a-zA-Z0-9-]+$/, 'Only letters, numbers, and hyphens allowed'),
});

type StoreForm = z.infer<typeof storeSchema>;

export default function StoreSelector() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<StoreForm>({
    resolver: zodResolver(storeSchema),
    defaultValues: {
      storeUrl: '',
    },
  });

  const storeUrlValue = watch('storeUrl');

  const onSubmit = async (data: StoreForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await validateOrganizationSlug(data.storeUrl);

      if (!result.valid) {
        setError(result.error || 'Store not found');
        setIsLoading(false);
        return;
      }

      // In development or Electron, set the org in localStorage and reload
      if (isDevelopment()) {
        setDevOrg(data.storeUrl.toLowerCase());
        
        // In Electron (file:// protocol), just reload - HashRouter will handle it
        if (isElectron()) {
          window.location.hash = '#/login';
          window.location.reload();
          return;
        }
        
        window.location.href = `${window.location.origin}/login?org=${data.storeUrl.toLowerCase()}`;
        return;
      }

      // In production, redirect to the subdomain
      redirectToSubdomain(data.storeUrl.toLowerCase());
    } catch (err) {
      console.error('Error validating store:', err);
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        <button
          type="button"
          onClick={showIntercomAnonymous}
          className="text-sm text-zinc-300 hover:text-white transition-colors flex items-center gap-1"
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Side - Store Selector Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white dark:bg-zinc-900">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                Log in to BlazePOS
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Enter your store URL to continue
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="storeUrl" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Store URL
                </Label>
                <div className="flex items-center gap-0">
                  <div className="relative flex-1">
                    <Input
                      id="storeUrl"
                      type="text"
                      placeholder="Enter your store URL"
                      className="h-11 px-4 pr-10 rounded-r-none border-r-0 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      {...register('storeUrl')}
                    />
                    <Store className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                  </div>
                  <div className="flex items-center h-11 px-4 bg-zinc-100 dark:bg-zinc-700 border border-l-0 border-zinc-300 dark:border-zinc-600 rounded-r-lg text-sm text-zinc-500 dark:text-zinc-400 font-medium whitespace-nowrap">
                    .blazepos.app
                  </div>
                </div>
                {errors.storeUrl && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.storeUrl.message}</p>
                )}
                {storeUrlValue && !errors.storeUrl && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    You'll be redirected to{' '}
                    <span className="font-medium text-zinc-700 dark:text-zinc-300">
                      {storeUrlValue.toLowerCase()}.blazepos.app
                    </span>
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-md shadow-blue-600/20"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                Forgot your store URL?
              </button>
            </p>

            <p className="mt-3 text-center text-sm text-zinc-500 dark:text-zinc-400">
              Don't have a store yet?{' '}
              <Link
                to="/register"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Get started
              </Link>
            </p>

            {/* Forgot Store URL Modal */}
            {showForgot && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setShowForgot(false); setForgotSent(false); }}>
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                  {forgotSent ? (
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-950/30 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">Check your email</h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                        If an account exists with that email, we sent your store URL.
                      </p>
                      <Button variant="outline" onClick={() => { setShowForgot(false); setForgotSent(false); }}>
                        Done
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-zinc-900 dark:text-white">Find your store</h3>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">We will email you your store URL</p>
                        </div>
                      </div>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!forgotEmail.trim()) return;
                        setForgotLoading(true);
                        try {
                          await supabase.functions.invoke('find-store', {
                            body: { email: forgotEmail.trim() },
                          });
                        } catch {
                          // Always show success
                        }
                        setForgotLoading(false);
                        setForgotSent(true);
                      }}>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="h-11 mb-3"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={() => setShowForgot(false)} className="flex-1">
                            Cancel
                          </Button>
                          <Button type="submit" disabled={forgotLoading || !forgotEmail.trim()} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                            {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 items-center justify-center p-12 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 text-center">
            {/* POS Terminal Illustration */}
            <div className="relative mx-auto w-80 h-80 mb-8">
              {/* Monitor */}
              <div className="absolute inset-x-4 top-0 h-56 bg-zinc-900 rounded-2xl shadow-2xl border-4 border-zinc-800 overflow-hidden">
                {/* Screen */}
                <div className="m-3 h-44 bg-zinc-800 rounded-lg overflow-hidden">
                  {/* Mock POS Interface */}
                  <div className="p-2 h-full flex">
                    {/* Sidebar */}
                    <div className="w-8 bg-zinc-700/50 rounded mr-2 flex flex-col gap-1 p-1">
                      <div className="w-full h-4 bg-orange-500/80 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                    </div>
                    {/* Main content */}
                    <div className="flex-1 grid grid-cols-3 gap-1">
                      <div className="bg-purple-500/60 rounded" />
                      <div className="bg-amber-500/60 rounded" />
                      <div className="bg-green-500/60 rounded" />
                      <div className="bg-blue-500/60 rounded" />
                      <div className="bg-rose-500/60 rounded" />
                      <div className="bg-cyan-500/60 rounded" />
                      <div className="bg-indigo-500/60 rounded" />
                      <div className="bg-orange-500/60 rounded" />
                      <div className="bg-teal-500/60 rounded" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Stand */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-16 bg-zinc-800 rounded-b-xl" />
              {/* Base */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-4 bg-zinc-900 rounded-full shadow-lg" />
            </div>

            <h2 className="text-2xl font-semibold text-white mb-3">
              Modern Point of Sale
            </h2>
            <p className="text-blue-100 text-lg max-w-sm mx-auto">
              Streamline your business with our powerful, intuitive POS system
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Real-time Analytics
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Multi-location
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Cloud-based
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Desktop & Mobile Apps
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Logistics & Inventory
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-50 dark:bg-zinc-800 border-t border-zinc-200 dark:border-zinc-700 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-400">
            <p>BlazePOS • Version {import.meta.env.VITE_APP_VERSION as string} • {new Date().getFullYear()} All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="https://www.blazepointofsale.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Privacy Policy
            </a>
            <a href="https://www.blazepointofsale.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
